/**
 * Single source of truth for "workout volume" on the client.
 *
 * Volume = Σ (kg × reps) over COMPLETED sets only. This mirrors the backend rule in
 * Planny.Web.Api → WorkoutPlanService.GetMyWorkoutsAsync ("Volume counts only
 * completed sets"), so the live "Общ обем" inside the workout dialog always matches
 * the server-computed total shown on the My Workouts cards. Both sides must stay in
 * lockstep — if one changes, change the other.
 *
 * Everything in the app that needs a workout/exercise/set volume should go through
 * these helpers rather than re-summing kg × reps inline.
 */

export interface IVolumeSet {
  kg: number;
  reps: number;
  /** Only completed sets count toward volume (a set that's filled in but not
   *  checked off is planned work, not logged work). */
  isCompleted: boolean;
}

export interface IVolumeExercise {
  sets: IVolumeSet[];
}

/** A single set's volume: kg × reps once it's completed, otherwise 0. */
export const setVolume = (set: IVolumeSet): number =>
  set.isCompleted ? (set.kg || 0) * (set.reps || 0) : 0;

/** Total volume of one exercise across its completed sets. */
export const exerciseVolume = (exercise: IVolumeExercise): number =>
  (exercise.sets ?? []).reduce((sum, s) => sum + setVolume(s), 0);

/** Total completed volume of a whole workout (Σ over all exercises). */
export const computeTotalVolume = (exercises: IVolumeExercise[]): number =>
  (exercises ?? []).reduce((sum, e) => sum + exerciseVolume(e), 0);

/**
 * Number of completed ("hard") working sets in one exercise. Unlike volume, this is
 * load-agnostic: a bodyweight set counts the same as a loaded one. It's the metric
 * used to express how training is *distributed* across muscle groups (see
 * {@link ../computeMuscleSplit.computeMuscleSplit}).
 */
export const exerciseCompletedSets = (exercise: IVolumeExercise): number =>
  (exercise.sets ?? []).filter((s) => s.isCompleted).length;

/** Total completed sets across a whole workout (Σ over all exercises). */
export const computeTotalCompletedSets = (exercises: IVolumeExercise[]): number =>
  (exercises ?? []).reduce((sum, e) => sum + exerciseCompletedSets(e), 0);
